import { Component, Input, Output, EventEmitter, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgSelectModule } from '@ng-select/ng-select';
import { faRocket, faTimes } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule } from '@ngx-translate/core';
import { KnowledgeBaseService, splitMethodsForFile, DEFAULT_CONTAINER_NAME } from '../../services/knowledge-base.service';
import { ConfigurationsService, Configuration } from '../../services/configurations.service';
import { AlertService } from '../alert-service/alert-service';
import { ButtonComponent } from '../button/button.component';

export interface DeployableArchive {
  id: string;
  name: string;
}

@Component({
  selector: 'app-modal-deploy-knowledge-base',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, NgSelectModule, TranslateModule, ButtonComponent],
  templateUrl: './modal-deploy-knowledge-base.component.html',
  styleUrls: ['./modal-deploy-knowledge-base.component.scss'],
})
export class ModalDeployKnowledgeBaseComponent implements OnInit, OnChanges {
  @Input() show = false;
  @Input() archive: DeployableArchive | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() deployed = new EventEmitter<string>();

  faRocket = faRocket;
  faTimes = faTimes;

  loading = false;
  name = '';
  splitMethod = '';
  splitMethods: string[] = [];
  regexPattern = '';
  containerName = DEFAULT_CONTAINER_NAME;

  delfosServerConfigs: Configuration[] = [];
  selectedDelfosServer = '';

  constructor(
    private knowledgeBaseService: KnowledgeBaseService,
    private configurationsService: ConfigurationsService,
    private alert: AlertService,
  ) {}

  ngOnInit(): void {
    this.configurationsService.listAllowedForMe().subscribe({
      next: res => { this.delfosServerConfigs = (res.configurations || []).filter(c => c.type === 'delfos_server'); },
      error: () => { this.delfosServerConfigs = []; },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['show']?.currentValue && this.archive) {
      this.name = this.stripExtension(this.archive.name);
      this.splitMethods = splitMethodsForFile(this.archive.name);
      this.splitMethod = this.splitMethods[0] || '';
      this.regexPattern = '';
      this.containerName = DEFAULT_CONTAINER_NAME;
      this.selectedDelfosServer = '';
      this.loading = false;
    }
  }

  get canDeploy(): boolean {
    if (!this.name?.trim() || !this.splitMethod || !this.containerName?.trim()) return false;
    if (this.splitMethod === 'regex' && !this.regexPattern.trim()) return false;
    return true;
  }

  private get effectiveSplitMethod(): string {
    return this.splitMethod === 'regex' ? this.regexPattern.trim() : this.splitMethod;
  }

  private stripExtension(fileName: string): string {
    return (fileName || '').replace(/\.[^./\\]+$/, '');
  }

  onClose(): void {
    this.close.emit();
    this.loading = false;
  }

  onDeploy(): void {
    if (!this.archive?.id || !this.canDeploy) return;

    this.loading = true;
    const deployedName = this.name.trim();
    this.knowledgeBaseService.deploy(this.archive.id, {
      name: deployedName,
      split_method: this.effectiveSplitMethod,
      delfos_server_config_id: this.selectedDelfosServer || undefined,
      container_name: this.containerName.trim(),
    }).subscribe({
      next: () => {
        this.alert.showSuccess('KNOWLEDGE_BASE.SUCCESS.DEPLOYED');
        this.deployed.emit(deployedName);
        this.onClose();
      },
      error: (err) => {
        this.alert.showError(err);
        this.loading = false;
      },
    });
  }
}
